import type {CallBackTarget, CallLeg, CallPanel, CallSurfaceView, EndedCall} from './types';

export interface CallSurfaceUi {
    readonly dialerOpen: boolean;
    readonly expanded: boolean;
    readonly panel: CallPanel;
    readonly ended?: EndedCall;
}

const PHASE_ORDER: Record<CallLeg['phase'], number> = {active: 0, calling: 1, incoming: 2};

const byPriority = (left: CallLeg, right: CallLeg): number =>
    Number(left.held) - Number(right.held) || PHASE_ORDER[left.phase] - PHASE_ORDER[right.phase];

export const buildCallSurfaceView = (legs: readonly CallLeg[], ui: CallSurfaceUi): CallSurfaceView => {
    const incoming = legs.filter(leg => leg.phase === 'incoming');
    const ongoing = legs.filter(leg => leg.phase !== 'incoming').sort(byPriority);
    const [primary, ...rest] = ongoing;
    if (incoming.length > 0) {
        return {kind: 'incoming', incoming, current: primary};
    }
    if (primary) {
        const expanded = primary.engine === 'group' ? !primary.minimized : ui.expanded;
        return {kind: 'call', primary, held: rest, panel: ui.panel, expanded};
    }
    if (ui.ended) {
        return {kind: 'ended', ended: ui.ended};
    }
    return ui.dialerOpen ? {kind: 'dialer'} : {kind: 'idle'};
};

export const isLiveView = (view: CallSurfaceView): boolean => view.kind === 'incoming' || view.kind === 'call';

const callBackOf = (leg: CallLeg): CallBackTarget | undefined => {
    if (leg.engine === 'matrix' && leg.peer) {
        return {kind: 'internal', peer: leg.peer, video: leg.kind === 'video'};
    }
    if (leg.engine === 'sip' && leg.dialNumber) {
        return {kind: 'phone', number: leg.dialNumber};
    }
    return undefined;
};

export const endedCallOf = (leg: CallLeg, endedAt: number): EndedCall => ({
    legId: leg.id,
    callBack: callBackOf(leg),
    engine: leg.engine,
    kind: leg.kind,
    identity: leg.identity,
    durationMs: leg.connectedAt ? Math.max(0, endedAt - leg.connectedAt) : 0,
    endedAt,
    roomId: leg.engine === 'sip' ? undefined : leg.roomId,
});

export const endedLegs = (previous: readonly CallLeg[], current: readonly CallLeg[]): readonly CallLeg[] => {
    const remaining = new Set(current.map(leg => leg.id));
    return previous.filter(leg => leg.phase !== 'incoming' && !remaining.has(leg.id));
};
